import React from "react";
import { Link } from "react-router-dom";
import {
  FiUsers,
  FiBriefcase,
  FiMessageSquare,
  FiTrendingUp,
  FiArrowRight,
  FiArrowDown,
  FiWifi,
  FiDollarSign,
  FiUserCheck,
  FiHeart,
  FiBookOpen,
  FiTarget,
  FiAward,
  FiCheckCircle,
} from "react-icons/fi";
import { Button } from "../components/ui";
import logo from "../Assets/Images/EMPACT LOGO.png";
import banner from "../Assets/Images/banner.jpg";

const FEATURES = [
  {
    icon: FiUsers,
    title: "Communities",
    description:
      "Join interest-based communities, meet fellow alumni, and collaborate on shared projects.",
  },
  {
    icon: FiTrendingUp,
    title: "Innovations",
    description:
      "Share your projects and ideas with the network and get discovered by peers and mentors.",
  },
  {
    icon: FiBriefcase,
    title: "Jobs",
    description:
      "Browse curated opportunities from partner organizations and apply in a couple of clicks.",
  },
  {
    icon: FiMessageSquare,
    title: "Community chat",
    description:
      "Keep the conversation going with real-time chat inside every community you join.",
  },
];

// The problem, in numbers — sourced from the EMPACT Alumni impact one-pager.
// Scale and contrast carry the weight here, not color — the lead stat is
// simply larger and white, not a different hue from the supporting two.
const LEAD_STAT = {
  value: "67%",
  label: "youth unemployment — the highest rate in Kenya",
};
const SUPPORTING_STATS = [
  { value: "70%", label: "of Kenyans are aged 35 years and below" },
  {
    value: "1M",
    label: "youth enter the labor market annually without any skills",
  },
];

// Grouped rather than listed flat — three categories that actually explain
// the shape of who's being served, not six disconnected facts in a row.
const TARGET_GROUPS = [
  {
    label: "Demographics",
    items: ["Youth aged 18–35 years", "50–50 gender balance"],
  },
  {
    label: "Circumstance",
    items: [
      "Food insecure households",
      "Urban informal settlements & Arid Counties",
    ],
  },
  {
    label: "Readiness",
    items: [
      "Possess basic digital skills",
      "Earning minimum wage within 6 months of graduation",
    ],
  },
];

const SOLUTION_STEPS = [
  {
    icon: FiWifi,
    text: "Provide safe workspaces equipped with stable power, internet and computers.",
  },
  { icon: FiDollarSign, text: "Ensure our participants get fair pay." },
  { icon: FiUserCheck, text: "Engage work providers." },
  { icon: FiHeart, text: "Offer coaching and mentorship." },
  {
    icon: FiBookOpen,
    text: "Offer basic and advanced training tracks delivered in-person, hybrid and online.",
  },
  {
    icon: FiUsers,
    text: "Build a community of EMPACT Alumni for peer support.",
  },
];

const THEORY_OF_CHANGE = [
  {
    step: "01",
    title: "Soft skills & Basic Track",
    description:
      "The youth come in not having skills (food insecure). We take them through the mandatory basic track.",
  },
  {
    step: "02",
    title: "Digital microwork training",
    description:
      "The youth are trained on more complex online work, and begin to earn as they learn.",
  },
  {
    step: "03",
    title: "Online freelancing",
    description:
      "The youth are now familiar with working online and able to do various online tasks and get work themselves.",
  },
];

// Animates a number from 0 up to `end` once the element scrolls into view —
// runs only once (won't re-trigger scrolling past it again), eased so it
// settles rather than stopping abruptly, and formats its own display
// string (decimals, thousands separators, prefix/suffix) so callers just
// hand it raw numeric data, not pre-formatted strings.
function useCountUp({ end, duration = 1400, decimals = 0 }) {
  const [value, setValue] = React.useState(0);
  const ref = React.useRef(null);
  const hasRun = React.useRef(false);

  React.useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting || hasRun.current) return;
        hasRun.current = true;

        const start = performance.now();
        const animate = (now) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          // Ease-out cubic — fast at first, settles gently into the final
          // number instead of stopping on a dime.
          const eased = 1 - Math.pow(1 - progress, 3);
          setValue(end * eased);
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
        observer.disconnect();
      },
      { threshold: 0.4 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [end, duration]);

  const formatted =
    decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString();

  return { ref, formatted };
}

function CountUpNumber({
  end,
  duration,
  decimals = 0,
  prefix = "",
  suffix = "",
}) {
  const { ref, formatted } = useCountUp({ end, duration, decimals });
  return (
    <span ref={ref}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

// Trims a trailing ".0" so a whole-number end of a range (e.g. "$3.0")
// reads as "$3" instead — only the fractional bound ($0.5) actually needs
// the decimal place shown.
//
// Bug fix: useCountUp is called twice here, and each call creates its own
// independent ref + IntersectionObserver — the previous version only
// attached one of the two refs (startCount.ref) to an actual DOM element,
// so endCount's observer never had a node to watch, its animation never
// triggered, and it stayed frozen at its initial value of 0 ("0.5 – 0"
// instead of "0.5 – 3"). Both refs now get their own real span to attach
// to, so both animations actually run.
function RangeValue({ rangeStart, rangeEnd, decimals, prefix, duration }) {
  const startCount = useCountUp({ end: rangeStart, duration, decimals });
  const endCount = useCountUp({ end: rangeEnd, duration, decimals: 0 });
  return (
    <span>
      <span ref={startCount.ref}>
        {prefix}
        {startCount.formatted}
      </span>
      {" – "}
      <span ref={endCount.ref}>
        {prefix}
        {endCount.formatted}
      </span>
    </span>
  );
}

// Structured as real numeric data (not pre-formatted strings) so each
// figure can actually be counted up to, rather than just displayed.
const METRICS = [
  {
    icon: FiAward,
    end: 2500,
    suffix: "+",
    label: "youth trained on digital work",
  },
  {
    icon: FiDollarSign,
    isRange: true,
    rangeStart: 0.5,
    rangeEnd: 3,
    decimals: 1,
    prefix: "$",
    label: "hourly earnings earned",
  },
  {
    icon: FiTrendingUp,
    end: 60,
    suffix: "%",
    label: "youth earning an income after training",
  },
  {
    icon: FiCheckCircle,
    end: 98,
    suffix: "%",
    label: "youth completing training",
  },
  {
    icon: FiTarget,
    end: 120000,
    prefix: "$",
    suffix: "+",
    label: "cumulative total earned during training",
  },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-white">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <img src={logo} alt="Empact Alumni" className="h-8 w-auto" />

        <nav className="flex items-center gap-4">
          <Link
            to="/game"
            className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-semibold text-primary-700 transition hover:border-primary-300 hover:bg-primary-100"
          >
            <span aria-hidden="true">🎮</span>
            Play Our Game
          </Link>

          <Link
            to="/login"
            className="text-sm font-medium text-slate-600 transition hover:text-slate-900"
          >
            Sign in
          </Link>

          {/* <Link to="/register">
            <Button size="sm">Get started</Button>
          </Link> */}
        </nav>
      </header>

      {/* Hero */}
      <section className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 px-6 py-14 lg:grid-cols-2 lg:py-24">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-px w-8 bg-primary-500" />
            <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Connecting food insecure youth to economic opportunity
            </span>
          </div>
          <h1 className="mt-4 text-4xl font-extrabold leading-tight text-slate-900 sm:text-5xl">
            Empowered <span className="text-primary-600">to empower.</span>
          </h1>
          <p className="mt-4 max-w-lg text-lg text-slate-500">
            A single home for alumni, innovators and organizations to build
            professional portfolios, share innovations, join communities and
            discover opportunities in the digital economy.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login">
              <Button size="lg">
                Sign In <FiArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            {/* <Link to="/login">
              <Button size="lg" variant="secondary">
                Sign in
              </Button>
            </Link> */}
          </div>
        </div>
        <div className="relative">
          <img
            src={banner}
            alt="Empact Alumni community"
            className="h-72 w-full rounded-xl object-cover shadow-soft lg:h-96"
          />
          <div className="absolute -bottom-6 -left-6 max-w-[220px] rounded-xl border border-slate-100 bg-white p-4 shadow-card">
            <p className="text-3xl font-extrabold text-primary-600">67%</p>
            <p className="mt-1 text-xs text-slate-500">
              youth unemployment — the highest rate in Kenya
            </p>
          </div>
        </div>
      </section>

      {/* The problem — a stark, pure-slate dark band. Contrast and scale
          carry the impact, not color; amber appears once, as a single
          thread that reappears at the Outcome banner further down. */}
      <section className="bg-primary-900 py-16 text-white sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <span className="text-xs font-semibold uppercase tracking-widest text-amber-400">
            The problem
          </span>
          <h2 className="mt-3 max-w-2xl text-2xl font-bold sm:text-3xl text-slate-300">
            Kenya's youth make up the majority of the active workforce yet face
            the highest unemployment rate in the country, causing frustration,
            desperation, and other mental health issues.
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-10 border-t border-white/10 pt-10 lg:grid-cols-[1.3fr_1fr]">
            <div className="border-l-2 border-amber-400 pl-6">
              <p className="text-7xl font-extrabold sm:text-8xl">
                {LEAD_STAT.value}
              </p>
              <p className="mt-3 max-w-xs text-base text-slate-300">
                {LEAD_STAT.label}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-1">
              {SUPPORTING_STATS.map((stat) => (
                <div
                  key={stat.label}
                  className="border-l-2 border-white/20 pl-6"
                >
                  <p className="text-3xl font-bold text-white">{stat.value}</p>
                  <p className="mt-2 text-sm text-slate-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who we serve — grouped into three categories that explain the
          actual shape of the target population, rather than six flat tags.
          Typographic hierarchy does the work; color appears once, as a
          small consistent marker, not per item. */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
            Who we serve
          </span>
          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Our target
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-10 sm:grid-cols-3">
            {TARGET_GROUPS.map((group) => (
              <div key={group.label}>
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary-500" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    {group.label}
                  </span>
                </div>
                <ul className="mt-4 space-y-3 border-l border-slate-200 pl-4">
                  {group.items.map((item) => (
                    <li
                      key={item}
                      className="text-[15px] leading-snug text-slate-700"
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The solution — a clean divided list rather than a grid of boxed
          cards. One consistent icon color throughout, no per-item
          variation, generous whitespace instead of backgrounds doing the
          separating. */}
      <section className="border-t border-slate-100 bg-slate-50 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
            The solution and how we do it
          </span>
          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            Provide critical services and infrastructure
          </h2>
          <div className="mt-10 grid grid-cols-1 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white sm:grid-cols-2 sm:divide-y-0">
            {SOLUTION_STEPS.map(({ icon: Icon, text }, index) => (
              <div
                key={text}
                className={`flex items-start gap-4 p-6 ${
                  index % 2 === 0 ? "sm:border-r sm:border-slate-200" : ""
                } ${index >= 2 ? "sm:border-t sm:border-slate-200" : ""}`}
              >
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
                <p className="text-sm leading-relaxed text-slate-700">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Theory of change — a real connected sequence. Neutral cards, one
          consistent numeral color, arrows carrying the progression. The
          Outcome banner picks the amber thread back up from the problem
          section, closing the arc. */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600">
            Theory of change
          </span>
          <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
            From basic skills to real income
          </h2>

          <div className="mt-10 flex flex-col items-stretch gap-4 lg:flex-row lg:items-center">
            {THEORY_OF_CHANGE.map((item, index) => (
              <React.Fragment key={item.step}>
                <div className="flex-1 rounded-2xl border border-slate-200 bg-white p-6">
                  <span className="text-4xl font-extrabold text-primary-600">
                    {item.step}
                  </span>
                  <h3 className="mt-2 text-lg font-semibold text-slate-900">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-500">
                    {item.description}
                  </p>
                </div>
                {index < THEORY_OF_CHANGE.length - 1 && (
                  <>
                    <FiArrowRight className="mx-auto hidden h-5 w-5 shrink-0 text-slate-300 lg:block" />
                    <FiArrowDown className="mx-auto block h-5 w-5 shrink-0 text-slate-300 lg:hidden" />
                  </>
                )}
              </React.Fragment>
            ))}
          </div>

          <div className="mt-10 rounded-2xl bg-slate-900 p-10 text-center">
            <span className="text-xs font-semibold uppercase tracking-widest text-amber-400">
              Outcome
            </span>
            <p className="mt-3 text-3xl font-bold italic text-white sm:text-4xl">
              A food secure youth.
            </p>
          </div>
        </div>
      </section>

      {/* Impact metrics — the one deliberate saturated-color moment on the
          page. Everything before this has been restrained specifically so
          this band actually lands. */}
      {/* Impact metrics — the one deliberate saturated-color moment on the
    page. Everything before this has been restrained specifically so
    this band actually lands. Figures count up from zero once scrolled
    into view. */}
      <section className="bg-primary-600 py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-100">
            Our metrics
          </span>
          <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">
            Real numbers, real impact
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {METRICS.map((metric) => (
              <div
                key={metric.label}
                className="rounded-2xl bg-white p-5 text-center shadow-card"
              >
                <metric.icon className="mx-auto h-6 w-6 text-primary-600" />
                <p className="mt-3 text-2xl font-extrabold text-slate-900">
                  {metric.isRange ? (
                    <RangeValue
                      rangeStart={metric.rangeStart}
                      rangeEnd={metric.rangeEnd}
                      decimals={metric.decimals}
                      prefix={metric.prefix}
                    />
                  ) : (
                    <CountUpNumber
                      end={metric.end}
                      prefix={metric.prefix}
                      suffix={metric.suffix}
                    />
                  )}
                </p>
                <p className="mt-1.5 text-xs text-slate-500">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's inside the platform itself */}
      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="text-center text-2xl font-bold text-slate-900">
            Everything you need to grow your network
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="rounded-xl border border-slate-200 bg-white p-6 shadow-card"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-[15px] font-semibold text-slate-900">
                  {title}
                </h3>
                <p className="mt-1.5 text-sm text-slate-500">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-8 text-center text-sm text-slate-400">
        Empact Alumni &copy; {new Date().getFullYear()}. All rights reserved.
      </footer>
    </div>
  );
}
